import React, { useState, useEffect, ChangeEvent } from "react";
import styled from "styled-components";
import { useMutation } from "react-query";

import { Col, Input, Row } from "antd";
import Modal from "../Modals/Modal";
import { searchAddress } from "./apis";
import { AddressType } from "./types";
import Pagination from "../Pagination";
import { theme } from "../../../theme";
import Badge from "../../atoms/Badge";
import { MIN_MORNITOR_WIDTH } from "../../constants";
import Explain from "../../atoms/Explain";
import { HttpError } from "../../../network/types";
import { tips } from "./constants";
import Button from "../../atoms/Button";

interface Props {
  setShowSearchAddress: () => void; // 이건 모달을 열었다 닫았다 할 수 있는 상태 값, 모달 내에 있는 기본 값을 사용하면 모든 창이 닫히기 때문에 상태값 사용
  setAddress: (e: AddressType) => void; // 얘는 주소검색이 필요한 곳에서 주소를 저장해주는 상태 값
}

const SearchAddress: React.FC<Props> = ({
  setShowSearchAddress,

  setAddress,
}) => {
  const [addressList, setAddressList] = useState<Array<any>>([]);
  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const pageSize = 30;
  const [keyword, setKeyword] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    if (totalCount !== 0) {
      getAdressList.mutate({ keyword, page, pageSize });
    }
  }, [page, pageSize]);
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    setErrorMsg("");
  };

  const getAdressList = useMutation(searchAddress, {
    onSuccess(data) {
      setAddressList(data.rows);
      setTotalCount(data.count);
    },
    onError(err: HttpError) {
      setErrorMsg(err.message);
      setTotalCount(0);
      setAddressList([]);
    },
  }); // 주소 목록 불러오기
  const onTypeEnter = (e: React.KeyboardEvent) => {
    setPage(1);
    if (e.key === "Enter") getAdressList.mutate({ keyword, page: 1, pageSize });
  };
  const renderAddressList = () => {
    switch (addressList.length) {
      case 0:
        return (
          <>
            <TipTitle>Tip</TipTitle>
            <Row gutter={[0, 16]}>
              {tips.map((val) => (
                <Col span={24} key={val.tip}>
                  <Row gutter={[0, 0]}>
                    <Col span={24}>
                      <MainTip>{val.tip}</MainTip>
                    </Col>
                    {val.subTip && (
                      <Col span={24}>
                        <SubTip>{val.subTip}</SubTip>
                      </Col>
                    )}
                  </Row>
                </Col>
              ))}
            </Row>
          </>
        );
      default:
        return addressList.map((data, index) => (
          <StyledListRow
            onClick={() => {
              setAddress({ ...data, detail: "" });
              setShowSearchAddress();
            }}
            key={index}
          >
            <ZipcodeWrapper>[{data.zipCode}]</ZipcodeWrapper>
            <Row gutter={[0, 8]}>
              <Col span={24}>
                <SearchInputWrapper>
                  <Badge>도로명</Badge>
                  <RoadAddressWrapper>{data.street}</RoadAddressWrapper>
                </SearchInputWrapper>
              </Col>
              <Col span={24}>
                <SearchInputWrapper>
                  <Badge>지번</Badge>
                  <GroundAddressWrapper>{data.zibun}</GroundAddressWrapper>
                </SearchInputWrapper>
              </Col>
            </Row>
          </StyledListRow>
        ));
    }
  };
  return (
    <Modal isNoFooter footer={null} title="주소 검색" width={700} okText="확인">
      <AddressWrap isData={addressList.length > 0} onKeyDown={onTypeEnter}>
        <SearchInputWrapper>
          <StyledTitle>
            주소 검색<RequiredStar>*</RequiredStar>
          </StyledTitle>
          <InputWrapper>
            <Row gutter={[0, 4]}>
              <Col span={24}>
                <Input
                  placeholder="주소를 입력해주세요"
                  value={keyword}
                  onChange={onChange}
                  disabled={getAdressList.isLoading}
                  autoFocus
                />
              </Col>
              {errorMsg !== "" && (
                <Col span={24}>
                  <Explain
                    status={errorMsg === "" ? undefined : "error"}
                    explain={errorMsg}
                  />
                </Col>
              )}
            </Row>
          </InputWrapper>
          <Button
            disabled={getAdressList.isLoading}
            onClick={() => {
              setPage(1);
              if (keyword.trim().length > 0)
                getAdressList.mutate({ keyword, page: 1, pageSize });
            }}
          >
            검색
          </Button>
        </SearchInputWrapper>
        <AddressListBox isData={addressList.length > 0}>
          {renderAddressList()}
        </AddressListBox>
        <PaginationWrapper>
          {totalCount > 0 ? (
            <Pagination
              currentPage={page || 1}
              pageSize={pageSize}
              totalCount={totalCount}
              setCurrentPage={setPage}
            />
          ) : (
            <StyledPaegWrapper />
          )}
        </PaginationWrapper>
      </AddressWrap>
    </Modal>
  );
};
export default SearchAddress;

const AddressWrap = styled.div<{ isData?: boolean }>`
  padding: 24px;
  box-sizing: border-box;
  background-color: white;
  width: 700px;
  position: relative;
  transition: height 1s;
  display: flex;
  border-radius: 0px 0px 8px 8px;
  flex-direction: column;
  justify-content: space_between;
  @media only screen and (max-width: ${MIN_MORNITOR_WIDTH}) {
    height: 100vh;
    max-width: 100%;
    justify-content: center;
    height: max-content;
    align-items: center;
    align-content: center;
  }
`;
const AddressListBox = styled.div<{ isData?: boolean }>`
  width: 100%;
  height: fit-content;
  transition: height 1s;
  border-radius: 4px;
  max-height: 370px;
  overflow: hidden;
  overflow-y: auto;
  transition: height 1s;
  height: 370px;
  border: 1px solid ${({ isData }) => (isData ? theme.colors.gray6 : "none")};
  padding: -1px 0px;
  margin: 24px 0px;
  @media only screen and (max-width: ${MIN_MORNITOR_WIDTH}) {
    max-width: 100%;
    height: 360px;
    justify-content: center;
    height: max-content;
    align-items: center;
    align-content: center;
  }
`;

const StyledListRow = styled.div`
  ${theme.typo.body.r16};
  padding: 20px 17px;
  cursor: pointer;
  line-height: 50px;

  border-bottom: 1px solid ${theme.colors.gray6};
`;
const StyledPaegWrapper = styled.div`
  min-height: 32px;
`;
const StyledTitle = styled.div`
  text-align: left;
  flex: 0 0 165px;
  display: flex;
  flex-wrap: nowrap;
  ${theme.typo.heading.b16};
  line-height: 32px;
  @media only screen and (max-width: ${MIN_MORNITOR_WIDTH}) {
    display: none;
  }
`;
const RequiredStar = styled.div`
  color: ${theme.colors.red3};
  margin-left: 4px;
`;
const PaginationWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;
const SearchInputWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: nowrap;
  align-items: flex-start;
`;
const InputWrapper = styled.div`
  margin-right: 8px;
  width: 100%;
`;
const RoadAddressWrapper = styled.div`
  ${theme.typo.body.r14};
  color: ${theme.colors.gray1};
  line-height: 24px;
`;
const GroundAddressWrapper = styled.div`
  ${theme.typo.body.r12};
  color: ${theme.colors.gray3};
  line-height: 20px;
`;
const ZipcodeWrapper = styled.div`
  color: ${theme.colors.gray1};
  margin-bottom: 8px;
  ${theme.typo.heading.b14};
  &:hover {
    text-decoration-line: underline;
  }
`;
const MainTip = styled.div`
  ${theme.typo.body.r12};
  color: ${theme.colors.gray1};
`;
const SubTip = styled.div`
  ${theme.typo.body.r12};
  color: #007aff;
`;
const TipTitle = styled.div`
  ${theme.typo.heading.b18};
  margin-bottom: 8px;
`;
